import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";


import { TRANSLATION_FAQ_CATEGORIES } from "@/content/translation-corpus/faq-defs";

export const Route = createFileRoute("/en/translation/faq/")({
  loader: async (): Promise<{ TRANSLATION_FAQ_EN: { q: string; a: string }[]; TRANSLATION_KEYWORDS_EN: string[] }> => {
    const [m0, m1] = await Promise.all([
      import("@/content/translation-corpus/faq-en"),
      import("@/content/translation-corpus"),
    ]);
    return { TRANSLATION_FAQ_EN: m0.TRANSLATION_FAQ_EN, TRANSLATION_KEYWORDS_EN: m1.TRANSLATION_KEYWORDS_EN };
  },
  component: TranslationFaqHubEn,
  head: ({ loaderData }) => {
    const { TRANSLATION_FAQ_EN = [], TRANSLATION_KEYWORDS_EN = [] } = loaderData ?? {};

    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: "Certified Translation Thailand FAQ 2026 — Fees, MFA Stamps & Apostille",
        description:
          "Expat guide to certified translation for use in Thailand — 28 languages, THB 200/400 MFA stamps, Amphur / Immigration / Land Office / DBD requirements and the 2026 Apostille switch.",
        path: "/en/translation/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/translation/faq" }],
        keywords: TRANSLATION_KEYWORDS_EN.slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Certified Translation FAQ", url: `${site}/en/translation/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(TRANSLATION_FAQ_EN)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
            keywords: TRANSLATION_KEYWORDS_EN.slice(0, 100).join(", "),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/translation/faq")) },
      ],
    };
  },
});

function TranslationFaqHubEn() {
  const { TRANSLATION_FAQ_EN, TRANSLATION_KEYWORDS_EN } = Route.useLoaderData() as { TRANSLATION_FAQ_EN: { q: string; a: string }[]; TRANSLATION_KEYWORDS_EN: string[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Certified Translation FAQ</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          Certified Translation in Thailand — {TRANSLATION_FAQ_EN.length} FAQs (2026)
        </h1>
        <p className="text-muted-foreground mb-8">
          Attorney-written answers on certification tiers, pricing, passport-matching name spelling,
          Thai authority filings and the 2026 Apostille transition.
        </p>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/translation/faq" className="text-primary underline">คำถามที่พบบ่อยเรื่องแปลเอกสาร →</a>
        </div>

        <section aria-label="Translation FAQ categories" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">Browse by topic</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {TRANSLATION_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/en/translation/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="font-semibold mb-1">{c.titleEn} →</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {TRANSLATION_FAQ_EN.map((item, i) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">End-to-end translation & legalization</h2>
          <p className="text-sm text-muted-foreground mb-4">
            28 languages → notarial certification → MFA legalization → destination embassy / Apostille → worldwide courier.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            Request a quote →
          </a>
        </section>
      
        <FaqSourceNote topic="translation" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
